import { formattingOnlyHtml, markQuotedTail, withoutQuotedTail } from '@/modules/sentinel-workspace/email-html';
import { TICKET_OUTCOME, type SentinelConversation, type SentinelMessageWireDto, type SentinelTicket, type SentinelTicketWireDto, type SentinelWorkspaceDetailWireDto, type WorkspaceMessage } from '@/modules/sentinel-workspace/types';

const TICKET_OUTCOME_BY_STATUS: Record<string, SentinelConversation['outcome']> = {
  unassigned_by_agent: TICKET_OUTCOME.unassigned,
  rejected_in_ingestion: TICKET_OUTCOME.notPickedUp,
};

function completedOutcome(dto: SentinelWorkspaceDetailWireDto): SentinelConversation['outcome'] {
  const current = dto.aiEventLogs.find((event) => event.id === dto.ticket.current_ai_event_log_id);
  if (isAutomationOwned(current)) return TICKET_OUTCOME.answered;
  const events = current?.extra_details?.['sentinelEvents'];
  if (Array.isArray(events)) {
    const completed = [...events]
      .reverse()
      .map(record)
      .find((event) => event?.['type'] === 'dispatch_completed');
    const payload = record(completed?.['payload']);
    if (payload?.['close'] === false) return TICKET_OUTCOME.answered;
  }
  return TICKET_OUTCOME.resolved;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/**
 * Whether legacy automation still owns this turn: it chose the turn, and no person has since sent
 * the reply it held back from the workspace (`dispatch_completed`). After that send the ticket
 * reads as any other a person answered, and the reply is not offered a second time.
 */
export function isAutomationOwned(event: SentinelWorkspaceDetailWireDto['aiEventLogs'][number] | undefined): boolean {
  const events = event?.extra_details?.['sentinelEvents'];
  if (!Array.isArray(events)) return false;
  const types = events.map((item) => record(item)?.['type']);
  return types.includes('automation_selected') && !types.includes('dispatch_completed');
}

/** The helpdesk ids of the replies automation sent, read from each run's automation_dispatch_completed event. */
function automationSentIds(events: SentinelWorkspaceDetailWireDto['aiEventLogs']): Set<string> {
  const ids = new Set<string>();
  for (const event of events) {
    const log = event.extra_details?.['sentinelEvents'];
    if (!Array.isArray(log)) continue;
    for (const item of log) {
      const entry = record(item);
      if (entry?.['type'] !== 'automation_dispatch_completed') continue;
      const id = record(entry['payload'])?.['providerMessageId'];
      if (typeof id === 'string' || typeof id === 'number') ids.add(String(id));
    }
  }
  return ids;
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** Read the subject from an email message. */
function emailSubject(message: SentinelMessageWireDto | null | undefined): string | null {
  return nullableString(record(message?.content_attributes?.['email'])?.['subject']);
}

/** Read the first available ticket subject. */
export function ticketSubject(
  dto: SentinelTicketWireDto,
  messages: (SentinelMessageWireDto | null | undefined)[] = [dto.latestMessage],
  providerSubject: unknown = null,
): string {
  const messageSubject = [...messages].reverse().map(emailSubject).find((subject) => subject !== null);
  return (
    nullableString(dto.conversation.additional_attributes?.['subject']) ??
    nullableString(dto.conversation.additional_attributes?.['mail_subject']) ??
    nullableString(providerSubject) ??
    messageSubject ??
    `Ticket #${ticketNumber(dto)}`
  );
}

/** The number agents see: Kim helpdesk tickets are keyed by conversations.id but shown by display_id. */
function ticketNumber(dto: SentinelTicketWireDto): string {
  return String(dto.source_provider === 'helpdesk' ? dto.conversation.display_id : dto.source_ticket_id);
}

export function ticketFromApi(dto: SentinelTicketWireDto): SentinelTicket {
  const contentAttributes = dto.latestMessage?.content_attributes ?? {};
  return {
    // The id arrives as a string from Postgres — the selected ticket is compared as a number.
    id: Number(dto.id),
    conversationId: dto.conversation_id,
    // Kept as a STRING to match the brand list's ids, which are strings because
    // business_units.id is a BIGINT. Comparing one as a number is a silent miss.
    businessUnitId: dto.conversation.bu_id == null ? null : String(dto.conversation.bu_id),
    sourceTicketId: dto.source_ticket_id,
    displayId: ticketNumber(dto),
    subject: ticketSubject(dto),
    category: dto.conversation.categories?.[0] ?? null,
    customerName:
      dto.conversation.contact?.name ||
      (typeof contentAttributes['sender_name'] === 'string' ? contentAttributes['sender_name'] : 'Customer'),
    preview: dto.latestMessage?.processed_message_content || dto.latestMessage?.content || '',
    lastActivityAt: dto.updated_at,
    ownerName: dto.owner ? dto.owner.display_name || dto.owner.name || null : null,
    ownerId: dto.owner ? Number(dto.owner.id) : null,
    routerState: dto.router_state ?? null,
  };
}

export function ticketsFromApi(dtos: SentinelTicketWireDto[]): SentinelTicket[] {
  return dtos.map(ticketFromApi);
}

function customerFromProvider(value: unknown, ticket: SentinelTicket): SentinelConversation['customer'] {
  const customer = record(value);
  return {
    name: stringValue(customer?.['name'], ticket.customerName),
    email: nullableString(customer?.['email']),
    segment: nullableString(customer?.['segment']),
    ltv: typeof customer?.['ltv'] === 'number' && Number.isFinite(customer['ltv']) ? customer['ltv'] : null,
    tier: nullableString(customer?.['tier']),
    history: nullableString(customer?.['history']),
    topCategory:
      typeof customer?.['topCategory'] === 'string'
        ? customer['topCategory']
        : typeof customer?.['top_category'] === 'string'
          ? customer['top_category']
          : ticket.category,
  };
}


function subscriptionItemFromProvider(value: unknown): SentinelConversation['subscriptions'][number]['items'][number] | null {
  const item = record(value);
  const id = item?.['id'] ?? item?.['subscription_id'];
  if (!item || (typeof id !== 'string' && typeof id !== 'number')) return null;
  const quantity = item['quantity'];
  return {
    id: String(id),
    name: stringValue(item['name'] ?? item['product_title'], 'Subscription'),
    status: stringValue(item['status'], 'UNKNOWN'),
    createdOn: stringValue(item['createdOn'] ?? item['created_at'], '—'),
    quantity: typeof quantity === 'number' ? quantity : null,
    amount: stringValue(item['amount'] ?? item['price']),
    amountLabel: stringValue(item['amountLabel'] ?? item['amount_label'], 'Amount'),
    details: [],
    cancellationReason: nullableString(item['cancellationReason'] ?? item['cancellation_reason']),
    cancellationDate: nullableString(item['cancellationDate'] ?? item['cancelled_at']),
  };
}

/** Reads whatever subscription blocks the provider sent; absent or malformed means none. */
export function subscriptionsFromProvider(value: unknown): SentinelConversation['subscriptions'] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const block = record(entry);
    const provider = nullableString(block?.['provider']);
    if (!block || !provider) return [];
    const items = Array.isArray(block['items']) ? block['items'] : [];
    const meta = Array.isArray(block['meta']) ? block['meta'] : [];
    return [{
      provider,
      count: typeof block['count'] === 'number' ? block['count'] : items.length,
      totalSpent: stringValue(block['totalSpent'] ?? block['total_spent'], '—'),
      meta: meta.flatMap((entryValue) => {
        const row = record(entryValue);
        const label = nullableString(row?.['label']);
        return label ? [{ label, value: stringValue(row?.['value'], '—') }] : [];
      }),
      items: items.flatMap((itemValue) => {
        const item = subscriptionItemFromProvider(itemValue);
        return item ? [item] : [];
      }),
    }];
  });
}

function orderFromProvider(value: unknown): SentinelConversation['order'] {
  const order = record(value);
  const shippingCost = record(order?.['shippingCost'] ?? order?.['shipping_cost']);
  const shippingAddress = record(order?.['shippingAddress'] ?? order?.['shipping_address']);
  const shipment = record(order?.['shipment']);
  const items = order?.['items'];
  const statuses = order?.['statuses'];
  const externalId = order?.['externalId'] ?? order?.['external_id'];
  const orderedOn = order?.['orderedOn'] ?? order?.['ordered_on'];
  // Only the identity is required — every other block is optional and rendered when present.
  if (typeof externalId !== 'string') return null;

  return {
    externalId,
    orderedOn: typeof orderedOn === 'string' ? orderedOn : '—',
    statuses: (Array.isArray(statuses) ? statuses : []).filter((status): status is string => typeof status === 'string'),
    items: (Array.isArray(items) ? items : []).flatMap((item) => {
      const itemValue = record(item);
      if (!itemValue) return [];
      return [{
        quantity: typeof itemValue['quantity'] === 'number' ? itemValue['quantity'] : 0,
        name: stringValue(itemValue['name']),
        price: stringValue(itemValue['price']),
      }];
    }),
    shippingCost: shippingCost && {
      label: stringValue(shippingCost['label']),
      amount: stringValue(shippingCost['amount']),
    },
    shippingAddress: shippingAddress && {
      name: stringValue(shippingAddress['name']),
      address1: stringValue(shippingAddress['address1']),
      address2: stringValue(shippingAddress['address2']),
      city: stringValue(shippingAddress['city']),
      country: stringValue(shippingAddress['country']),
      zipcode: stringValue(shippingAddress['zipcode']),
    },
    shipment: shipment && {
      provider: stringValue(shipment['provider']),
      trackingId: stringValue(shipment['trackingId'] ?? shipment['tracking_id']),
      link: stringValue(shipment['link']),
    },
    totalSpent: stringValue(order?.['totalSpent'] ?? order?.['total_spent'], '—'),
  };
}

/** Build the known email headers. */
function emailHeader(
  dto: SentinelWorkspaceDetailWireDto,
  message: SentinelMessageWireDto,
  storedSubject: string | null,
): WorkspaceMessage['email'] {
  const channel = nullableString(message.additional_attributes?.['channel_type']);
  if (channel && channel.trim().toLowerCase() !== 'email') return undefined;
  const header = {
    from: nullableString(dto.ticket.conversation.contact?.email),
    to: nullableString(dto.ticket.conversation.additional_attributes?.['support_address']),
    subject: emailSubject(message) ?? storedSubject,
  };
  return header.from || header.to || header.subject ? header : undefined;
}

/**
 * A public email's stored HTML, `content_attributes.email.html_content.full` with the quoted reply
 * chain cut off, so a bubble does not repeat the thread above it. Covers both the customer's
 * incoming message and an agent's outgoing reply.
 *
 * `full` is read rather than `reply` because no helpdesk we sync stores `reply` as HTML: Kim
 * Helpdesk fills it with Html2Text output and Gorgias with `body_text`. Rendering either would drop
 * the images and formatting this field exists to show.
 *
 * Private notes are never email and keep rendering from `markdown`. A customer's email keeps its
 * quote, marked, for the bubble's "…" toggle; an agent's reply loses it.
 */
function emailHtml(dto: SentinelMessageWireDto): { html: string; hasQuote: boolean } | null {
  if (dto.message_type !== 0 && (dto.message_type !== 1 || dto.private)) return null;
  const full = record(record(dto.content_attributes?.['email'])?.['html_content'])?.['full'];
  // Intercom (and plain-text Gorgias mail) store bare text here; as HTML its line breaks would collapse.
  if (typeof full !== 'string' || !/<[a-z][^>]*>/i.test(full)) return null;
  // Sanitised once here rather than on every render of the bubble.
  return dto.message_type === 1 ? { html: formattingOnlyHtml(withoutQuotedTail(full)), hasQuote: false } : markQuotedTail(full);
}

function messageFromWire(
  dto: NonNullable<SentinelTicketWireDto['latestMessage']>,
  ticketId: number,
  email?: WorkspaceMessage['email'],
  automationSent: ReadonlySet<string> = new Set(),
  isKimHelpdesk = false,
): WorkspaceMessage {
  // Kim Helpdesk sends record Candy's own message id; every other helpdesk's records its id for the message.
  const sentId = isKimHelpdesk ? String(dto.id) : dto.source_id;
  const contentAttributes = dto.content_attributes ?? {};
  const html = emailHtml(dto);
  return {
    ...(email ? { email } : {}),
    ...(html ? { html: html.html } : {}),
    ...(html?.hasQuote ? { hasQuotedTail: true } : {}),
    id: dto.id,
    markdown: dto.processed_message_content || dto.content || '',
    from: dto.message_type === 0 ? 'customer' : 'agent',
    isPrivate: dto.private,
    sourceId: dto.source_id ?? null,
    // Only a reply an automation send produced: the helpdesk sends Kim's and a Sentinel's replies as the same user.
    isAutomated:
      dto.message_type !== 0 && !dto.private && (dto.automated === true || (sentId != null && automationSent.has(sentId))),
    senderName: typeof contentAttributes['sender_name'] === 'string' ? contentAttributes['sender_name'] : dto.sender_type || 'Unknown',
    avatarUrl: typeof contentAttributes['avatar_url'] === 'string' ? contentAttributes['avatar_url'] : null,
    createdAt: dto.created_at,
    attachments: (dto.attachments ?? []).map((attachment) => ({
      ...attachment,
      ticketId,
      messageId: dto.id,
    })),
  };
}

/**
 * A thread on its own, as a follow-up's details send it: no ticket around it, so no email header or
 * automation marks. Attachments are fetched through their Sentinel ticket, so without one they are left out.
 */
export function threadFromWire(messages: SentinelMessageWireDto[], ticketId: number | null, isKimHelpdesk: boolean): WorkspaceMessage[] {
  return messages
    .filter((message) => message.message_type === 0 || message.message_type === 1)
    .map((message) =>
      ticketId === null
        ? { ...messageFromWire(message, 0, undefined, undefined, isKimHelpdesk), attachments: [] }
        : messageFromWire(message, ticketId, undefined, undefined, isKimHelpdesk),
    );
}

export function workspaceFromApi(dto: SentinelWorkspaceDetailWireDto): SentinelConversation {
  const ticket = ticketFromApi(dto.ticket);
  const wireMessages = dto.messages?.length
    ? dto.messages.filter((message) => message.message_type === 0 || message.message_type === 1)
    : dto.ticket.latestMessage
      ? [dto.ticket.latestMessage]
      : [];
  const storedSubject =
    nullableString(dto.ticket.conversation.additional_attributes?.['subject']) ??
    nullableString(dto.providerContext?.['subject']);
  const isKimHelpdesk = dto.ticket.source_provider === 'helpdesk';
  const automationSent = automationSentIds(dto.aiEventLogs);
  const messages = wireMessages.map((message) =>
    messageFromWire(
      message,
      Number(dto.ticket.id),
      message.message_type === 0 ? emailHeader(dto, message, storedSubject) : undefined,
      automationSent,
      isKimHelpdesk,
    ),
  );
  const sent = dto.ticket.status === 'completed'
    ? dto.aiEventLogs.find((event) => event.id === dto.ticket.current_ai_event_log_id)
    : null;
  // The provider syncs the sent reply back as a normal message, but only after a delay. Show
  // the stored reply until that arrives, matched on the message's provider id so it is never shown
  // twice. source_id is the fallback for an older backend; on Richpanel it is Kim's content hash,
  // never the comment id a send records (tickets 9575, 9584).
  const sentReplySynced =
    dto.ticket.provider_message_id != null &&
    wireMessages.some(
      (message) => (message.provider_message_id ?? message.source_id) === dto.ticket.provider_message_id,
    );
  // Kim Helpdesk replies are read straight from its own database, so the real message is already
  // here. Showing the stored copy too would show the reply twice.
  const pendingProviderReply =
    dto.ticket.mode === 'live' && !isKimHelpdesk && !sentReplySynced ? sent?.response_message : null;
  if (pendingProviderReply && sent) {
    const automated = isAutomationOwned(sent);
    messages.push({
      id: -sent.id,
      markdown: pendingProviderReply,
      from: 'agent',
      isAutomated: automated,
      senderName: automated ? 'Automation' : 'Sentinel',
      createdAt: dto.ticket.updated_at,
    });
  }

  return {
    id: Number(dto.ticket.id),
    conversationId: dto.ticket.conversation_id,
    displayId: ticket.displayId,
    subject: ticketSubject(dto.ticket, wireMessages, dto.providerContext?.['subject']),
    category: ticket.category,
    messages,
    customer: customerFromProvider(dto.providerContext?.['customer'], ticket),
    order: orderFromProvider(dto.providerContext?.['order']),
    subscriptions: subscriptionsFromProvider(dto.providerContext?.['subscriptions']),
    outcome:
      dto.ticket.status === 'completed'
        ? completedOutcome(dto)
        : TICKET_OUTCOME_BY_STATUS[dto.ticket.status] ?? null,
  };
}
